import { validateDocument } from './document.mjs'

// Commit a unit and its incident edges together, after validating the final ranges.
export function updateUnitAnnotation(document, id, patch, relations, schemas, links) {
  const next = JSON.parse(JSON.stringify(document))
  updateUnit(next, id, patch)
  if (relations !== undefined) {
    const original = new Map(document.edges.map((edge) => [edge.id, edge]))
    const units = new Set(next.units.map((unit) => unit.id))
    const retained = next.edges.filter(
      (edge) => edge.level !== 'unit' || (edge.source !== id && edge.target !== id),
    )
    const ids = new Set(
      [...next.paragraphs, ...next.units, ...next.chapters, ...retained].map((item) => item.id),
    )
    const signatures = new Set()
    const updated = relations
      .map((value) => {
        const edge = JSON.parse(JSON.stringify(value))
        const previous = original.get(edge.id)
        const unchanged = JSON.stringify(previous) === JSON.stringify(edge)
        if (
          edge.level !== 'unit' ||
          (edge.source !== id && edge.target !== id) ||
          edge.source === edge.target
        )
          throw new Error('关系必须连接当前单元与另一个论证单元。')
        if (!units.has(edge.source) || !units.has(edge.target)) {
          // Range edits already remove edges of absorbed units; do not restore them.
          if (unchanged) return null
          throw new Error('关联单元不存在或已被当前句子范围合并，请修改或删除对应关系。')
        }
        if (!links.some((type) => type.id === edge.type)) throw new Error('请选择有效的关系类型。')
        const signature = JSON.stringify([edge.source, edge.target, edge.type])
        if (signatures.has(signature)) throw new Error('这条关系已存在。')
        signatures.add(signature)
        if (ids.has(edge.id)) throw new Error('关系编号重复。')
        ids.add(edge.id)
        if (!unchanged) {
          edge.provenance = 'manual'
          if (previous && (previous.source !== edge.source || previous.target !== edge.target))
            for (const key of [
              'evidence',
              'evidence_quote',
              'evidenceSource',
              'provenanceId',
              'discourse_relation',
            ])
              delete edge[key]
        }
        return edge
      })
      .filter(Boolean)
    next.edges = [...retained, ...updated]
  }
  return validateDocument(next, schemas, links)
}

export function updateUnit(document, id, patch) {
  const current = document.units.find((u) => u.id === id)
  const originalRanges = new Map(document.units.map((u) => [u.id, JSON.stringify(u.sentenceIds)]))
  const paragraph = document.paragraphs.find((p) => p.id === current.paragraphId)
  const sentenceIds = paragraph.sentences.map((s) => s.id),
    selected = new Set(patch.sentenceIds)
  const units = [],
    removed = new Set(),
    usedIds = new Set(document.units.map((u) => u.id))
  let counter = 1
  const newId = () => {
    while (usedIds.has(`U${String(counter).padStart(3, '0')}`)) counter++
    const next = `U${String(counter++).padStart(3, '0')}`
    usedIds.add(next)
    return next
  }
  // Preserve complete sentence coverage when a unit absorbs or releases sentences.
  for (const unit of document.units) {
    if (unit.paragraphId !== current.paragraphId) {
      units.push(unit)
      continue
    }
    if (unit.id !== id && !unit.sentenceIds.some((s) => selected.has(s))) {
      units.push(unit)
      continue
    }
    const remaining = unit.sentenceIds.filter((s) => !selected.has(s))
    if (unit.id === id) units.push({ ...unit, ...patch, provenance: 'manual', typeStatus: 'ready' })
    else if (!remaining.length) removed.add(unit.id)
    const runs = []
    remaining.forEach((s) => {
      const last = runs.at(-1)
      if (last && sentenceIds.indexOf(s) === sentenceIds.indexOf(last.at(-1)) + 1) last.push(s)
      else runs.push([s])
    })
    runs.forEach((run, i) =>
      units.push({
        ...unit,
        id: unit.id !== id && i === 0 ? unit.id : newId(),
        sentenceIds: run,
        provenance: 'manual',
      }),
    )
  }
  const paragraphOrder = new Map(document.paragraphs.map((p, i) => [p.id, i]))
  const sentenceOrder = new Map(document.paragraphs.flatMap((p) => p.sentences).map((s, i) => [s.id, i]))
  document.units = units.sort(
    (a, b) =>
      paragraphOrder.get(a.paragraphId) - paragraphOrder.get(b.paragraphId) ||
      sentenceOrder.get(a.sentenceIds[0]) - sentenceOrder.get(b.sentenceIds[0]),
  )
  // Reindex only changed ranges; changing a note or type must not discard the global index.
  let rangesChanged = removed.size > 0
  for (const unit of document.units.filter((u) => u.paragraphId === current.paragraphId)) {
    if (originalRanges.get(unit.id) === JSON.stringify(unit.sentenceIds)) continue
    rangesChanged = true
    for (const key of [
      'facets',
      'timeSpans',
      'semanticFacets',
      'embeddingKey',
      'embeddingModel',
      'facetSchemaVersion',
      'attributeDefinitionHash',
      'facetSourceHash',
      'summarySourceHash',
      'facetInputs',
      'entityInputHash',
      'nodeInputHash',
    ])
      delete unit[key]
  }
  if (rangesChanged) {
    if (document.globalIndexStatus === 'ready') document.globalIndexStatus = 'stale'
    for (const key of ['facets', 'timeSpans', 'timeRange', 'facetProvenance']) delete paragraph[key]
    paragraph.unitRelationsStatus = 'pending'
  }
  if (document.relationCandidates) document.relationCandidates = Object.fromEntries(
    Object.entries(document.relationCandidates).filter(([, r]) => !removed.has(r.a) && !removed.has(r.b)),
  )
  delete document.analysisFocus
  document.edges = document.edges.filter((e) => !removed.has(e.source) && !removed.has(e.target))
}
